import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChevronDownIcon, ShieldCheckIcon } from 'lucide-react';
import { checkoutUrl, checkoutUrlWithBump, images, kitPriceValue } from '../data/content';
import { upsell } from '../data/upsell';
import { trackCustomPixelEvent, trackInitiateCheckout } from '../utils/metaPixel';
import { rewarmCheckoutConnection } from '../utils/preconnect';
import { withUtms } from '../utils/withUtms';
import { CheckoutCover } from './CheckoutCover';
import { OfferCountdown } from './OfferCountdown';
import { UpsellModal } from './UpsellModal';
export function PurchaseSection() {
  const [isGuaranteeOpen, setIsGuaranteeOpen] = useState(false);
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const isLeavingRef = useRef(false);

  // iOS Safari freezes this page in the back-forward cache instead of unloading
  // it, so coming back from Shopify restores the old React state verbatim. That
  // left `isLeavingRef` stuck at true and every later click silently ignored.
  // Re-arm the buttons (and the page scroll) whenever the page is restored.
  useEffect(() => {
    const reset = () => {
      isLeavingRef.current = false;
      setIsUpsellOpen(false);
      setCoverUrl(null);
      document.body.style.overflow = '';
    };

    // A page restored from bfcache must never come back still covered.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) reset();
    };
    reset();
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('popstate', reset);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('popstate', reset);
    };
  }, []);

  // The cover goes up and the modal comes down in ONE commit — flushSync forces
  // that paint before the navigation starts, so there is no frame in which the
  // landing page shows through. Nothing is awaited in between.
  const goTo = (url: string) => {
    if (isLeavingRef.current) return;
    isLeavingRef.current = true;
    const destination = withUtms(url);
    flushSync(() => {
      setCoverUrl(destination);
      setIsUpsellOpen(false);
    });
    document.body.style.overflow = '';
    window.location.href = destination;

    // Safety net: if the navigation is blocked or cancelled, don't leave the
    // buttons dead. Two seconds is far longer than a real redirect takes.
    window.setTimeout(() => {
      isLeavingRef.current = false;
    }, 2000);
  };

  // The button never navigates: it opens the offer, which owns both destinations.
  const handleCheckoutClick = () => {
    // Warm the socket now, while the offer is on screen, so the handshake is
    // already done by the time either of its buttons navigates.
    rewarmCheckoutConnection();
    trackInitiateCheckout();
    setIsUpsellOpen(true);
    trackCustomPixelEvent('ViewUpsell', {
      content_name: upsell.title
    });
  };
  const handleUpsellAccept = () => {
    trackCustomPixelEvent('AcceptUpsell', {
      content_name: upsell.title,
      value: kitPriceValue + upsell.priceValue,
      currency: 'EUR'
    });
    setIsUpsellOpen(false);
    goTo(checkoutUrlWithBump);
  };
  const handleUpsellDecline = () => {
    setIsUpsellOpen(false);
    goTo(checkoutUrl);
  };
  return <section id="buy" className="border-y border-cream-300 bg-white px-6 py-12" data-id="element-112" data-mp="2fc9te91jy2o2">
      <img src={images.heroBox} alt="Scatola del Mega Kit Piccolo Ingegnere con centinaia di progetti da costruire con materiali che hai già in casa" className="mx-auto w-full max-w-[420px]" loading="lazy" data-id="element-113" data-mp="21d0f5ewwpimq" />

      <p className="mt-8 text-[12px] font-semibold uppercase tracking-[0.16em] text-ink-500" data-id="element-114" data-mp="1ri8eu3bxwho9">
        +3.369 download
      </p>
      <h2 className="mt-2.5 font-display text-[30px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink-900" data-id="element-115" data-mp="3luf240i3yowx">
        Mega Kit Piccolo Ingegnere
      </h2>

      <div className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-2" data-id="element-116" data-mp="3qszvbfyy08bv">
        <span className="font-display text-[28px] font-semibold tracking-[-0.02em] text-ink-900" data-id="element-117" data-mp="1qq7bz00fapqi">
          27,99 €
        </span>
        <span className="text-[16px] text-ink-500 line-through" data-id="element-118" data-mp="1lsynyislfrhc">55,98 €</span>
        <span className="rounded-md bg-gold-400/15 px-2 py-[3px] text-[12px] font-semibold uppercase tracking-[0.08em] text-gold-600" data-id="element-119" data-mp="1hp92413azo00">
          -50%
        </span>
      </div>

      <button type="button" onClick={handleCheckoutClick} className="btn-cta mt-7 block w-full rounded-xl px-6 py-4 text-center font-display text-[17px] font-semibold tracking-[0.02em] text-white transition-[background-image] focus:outline-none focus-visible:ring-4 focus-visible:ring-grass-500/30" data-id="element-120" data-mp="17snx3fraacw6">
        Aggiungi al carrello
      </button>

      <p className="mt-3 flex items-center justify-center gap-2 text-[13.5px] text-ink-500" data-id="element-121" data-mp="tywnk7djyt0g">
        <ShieldCheckIcon className="h-4 w-4 text-forest-500" aria-hidden="true" data-id="element-122" data-mp="p14vndcwr6w9" />
        Accesso immediato via email · pagamento sicuro
      </p>
      <p className="mt-2 text-center text-[12.5px] text-ink-500" data-id="element-123" data-mp="2vqop4u74w4hc">
        Prodotto 100% digitale: non ti spediamo nulla, scarichi tutto subito.
      </p>

      <p className="mt-8 flex items-center justify-center gap-2.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-ink-900" data-id="element-124" data-mp="30nf7xpabpe6g">
        <span className="h-1.5 w-1.5 rounded-full bg-gold-400/60" aria-hidden="true" data-id="element-125" data-mp="39pode0qqihcz" />
        <span className="text-gold-500" data-id="element-126" data-mp="2cznugid1qv3l">Prezzo di lancio</span>, ancora per poco
        <span className="h-1.5 w-1.5 rounded-full bg-gold-400/60" aria-hidden="true" data-id="element-127" data-mp="2cxkup6m3dnlo" />
      </p>

      <div className="mt-3" data-id="element-128" data-mp="2hwo0it9wbxup">
        <OfferCountdown data-id="element-129" data-mp="2m0m01534vaoa" />
      </div>

      <div className="mt-6 border-t border-cream-300 pt-2" data-id="element-130" data-mp="2lzkc4010qtp5">
        <button type="button" onClick={() => setIsGuaranteeOpen(open => !open)} aria-expanded={isGuaranteeOpen} className="flex w-full items-center gap-3 py-4 text-left" data-id="element-131" data-mp="2qqu0kxatzqzg">
          <span className="flex-1 text-[16px] font-medium text-ink-900" data-id="element-132" data-mp="1u73f83q9soi9">
            Soddisfatti o rimborsati, 7 giorni
          </span>
          <ChevronDownIcon className={`h-5 w-5 shrink-0 text-ink-500 transition-transform ${isGuaranteeOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-133" data-mp="31l5fd2n8qtk6" />
        </button>
        {isGuaranteeOpen ? <p className="pb-4 pr-2 text-[15.5px] leading-[1.65] text-ink-500" data-id="element-134" data-mp="3bb8l83fgo1zx">
            Metti le mani in pasta con tuo figlio per 7 giorni. Se non è quello che fa per la tua
            famiglia, scrivici e ti rimborsiamo fino all’ultimo centesimo. I bonus restano tuoi.
          </p> : null}
      </div>

      <UpsellModal isOpen={isUpsellOpen} onAccept={handleUpsellAccept} onDecline={handleUpsellDecline} data-id="element-135" data-mp="2dnzunu0ls2r2" />

      <CheckoutCover isVisible={coverUrl !== null} fallbackUrl={coverUrl ?? checkoutUrl} data-id="element-136" data-mp="2roaj6qefx592" />
    </section>;
}